import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import BookingFlow from "@/components/BookingFlow";
import { Snowflake, Wind, Thermometer, Phone, Clock, MapPin, Car, ShieldCheck, Gauge, ChevronRight, CalendarCheck, User, LogOut, ChevronDown, CalendarDays } from "lucide-react";
import { WEEKDAYS_FULL, getOpeningStatus } from "@/lib/dates";
import Logo from "@/components/Logo";
import { useCustomerAuth } from "@/context/CustomerAuth";
import AuthDialog from "@/components/AuthDialog";

const HERO_IMG = "https://images.unsplash.com/photo-1542399204-b8dd4af5113d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const VENT_IMG = "https://images.unsplash.com/photo-1542399204-b8dd4af5113d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const PROMO_VIDEO = "https://customer-assets.emergentagent.com/job_recharge-rapide-clim/artifacts/hyk4hb8d_Promo-CLIMOMAX13-paysage.mp4";
const BG_VIDEO = "https://customer-assets.emergentagent.com/job_recharge-rapide-clim/artifacts/uw5ms472_back%20site%20CLIMOMAX.mp4";
const MSC_LOGO = "https://customer-assets.emergentagent.com/job_recharge-rapide-clim/artifacts/t04jra9z_image.png";

function OpeningStatus({ settings, className = "" }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!settings) return null;
  const status = getOpeningStatus(settings);
  if (!status.label) return null;
  const color = status.open ? (status.soon ? "#FFD60A" : "#06D6A0") : "#EF476F";
  return (
    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border ${className}`}
      style={{ borderColor: `${color}55`, background: `${color}14` }} data-testid="opening-status">
      <span className="relative flex h-2 w-2">
        {status.open && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: color }} />}
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }} />
      </span>
      <span className="text-xs font-bold whitespace-nowrap" style={{ color }}>{status.label}</span>
    </div>
  );
}

const ROT_FALLBACK = ["Recharge de gaz", "Recherche de fuite", "Diagnostic clim", "Désinfection circuit"];

function RotatingService({ services }) {
  const items = services && services.length ? services.map((s) => s.name) : ROT_FALLBACK;
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setI((p) => (p + 1) % items.length), 2200);
    return () => clearInterval(id);
  }, [items.length]);
  const idx = i % items.length;
  return (
    <span key={idx} className="text-[#00B4D8] inline-block animate-rotate-word" data-testid="rotating-service">
      {items[idx]}
    </span>
  );
}

export default function Landing() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bookingRef = useRef(null);
  const { customer, logout } = useCustomerAuth();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    if (menuOpen) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);

  const chooseService = (s) => {
    setSelected(s);
    setTimeout(() => bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white">
      {/* NAV */}
      <nav className="sticky top-0 z-50 glass border-b border-[#1A2642]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="brand-logo-link">
            <Logo className="h-9 sm:h-10 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <OpeningStatus settings={settings} className="hidden md:inline-flex" />
            <a href="tel:0754896756" className="hidden lg:flex items-center gap-2 text-sm font-semibold text-[#94A3B8] hover:text-white transition">
              <Phone className="w-4 h-4 text-[#FFD60A]" /> 07 54 89 67 56
            </a>

            {customer ? (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setMenuOpen((o) => !o)} data-testid="account-menu-btn"
                  className="flex items-center gap-2 border border-[#1A2642] hover:border-[#00B4D8] rounded-full pl-1 pr-2.5 py-1 transition">
                  <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#0077B6] text-[#050810] flex items-center justify-center text-xs font-black uppercase" data-testid="account-avatar">
                    {(customer.first_name || "?").charAt(0)}
                  </span>
                  <span className="text-sm font-bold text-white max-w-[90px] truncate" data-testid="account-firstname">{customer.first_name}</span>
                  <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-52 glass border border-[#1A2642] rounded-lg overflow-hidden shadow-2xl" data-testid="account-menu">
                    <div className="px-4 py-3 border-b border-[#1A2642]">
                      <p className="text-sm font-bold text-white truncate">{customer.name || customer.first_name}</p>
                      <p className="text-xs text-[#94A3B8] truncate">{customer.email}</p>
                    </div>
                    <a href={`/gerer/${customer.manage_token}`} data-testid="account-bookings-link"
                      className="flex items-center gap-2 px-4 py-3 text-sm text-white hover:bg-[#00B4D8]/10 transition">
                      <CalendarDays className="w-4 h-4 text-[#00B4D8]" /> Mes réservations
                    </a>
                    <button onClick={() => { logout(); setMenuOpen(false); }} data-testid="account-logout"
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#EF476F] hover:bg-[#EF476F]/10 transition">
                      <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="login-btn"
                className="flex items-center gap-2 border border-[#1A2642] hover:border-[#00B4D8] text-white font-semibold text-sm rounded-full px-3 sm:px-4 py-2 transition">
                <User className="w-4 h-4 text-[#00B4D8]" /> <span className="hidden sm:inline">Se connecter</span>
              </button>
            )}

            <button onClick={() => bookingRef.current?.scrollIntoView({ behavior: "smooth" })} data-testid="nav-book-btn"
              className="flex items-center gap-2 bg-[#FFD60A] text-[#050810] font-bold text-sm rounded-full px-4 py-2 hover:bg-[#FFEA00] transition">
              <CalendarCheck className="w-4 h-4" /> Réserver
            </button>
          </div>
        </div>
        {/* Mobile status row */}
        <div className="md:hidden px-5 pb-2 -mt-1">
          <OpeningStatus settings={settings} />
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden frost-grid">
        {/* Background promo video */}
        <div className="absolute inset-0">
          <video src={BG_VIDEO} poster={HERO_IMG} autoPlay muted loop playsInline data-testid="hero-bg-video"
            className="w-full h-full object-cover object-center opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050810] via-[#050810]/85 to-[#050810]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050810]/40 via-transparent to-[#050810]" />
          <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-[#00B4D8]/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left: text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#00B4D8]/10 border border-[#00B4D8]/30 rounded-full px-4 py-1.5 mb-6 animate-float-up">
                <MapPin className="w-3.5 h-3.5 text-[#00B4D8]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8]">Istres · Bouches-du-Rhône</span>
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] animate-float-up drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
                Votre clim auto,<br /><span className="text-[#FFD60A]">réservez en ligne</span>.
              </h1>
              <div className="mt-5 inline-flex items-center gap-2.5 bg-[#0C1322]/70 border border-[#1A2642] rounded-full pl-4 pr-5 h-11 overflow-hidden animate-float-up">
                <Wind className="w-4 h-4 text-[#00B4D8] shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] shrink-0">Prestation</span>
                <span className="w-px h-4 bg-[#1A2642] shrink-0" />
                <span className="whitespace-nowrap text-sm sm:text-base font-bold">
                  <RotatingService services={services} />
                </span>
              </div>
              <p className="mt-6 text-base md:text-lg text-[#CBD5E1] max-w-xl animate-float-up">
                Recharge de gaz, recherche de fuite, diagnostic et désinfection. Réservez votre créneau en ligne, en quelques clics. Pour tous véhicules.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8 animate-float-up">
                <button onClick={() => document.getElementById("prestations")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-book-btn"
                  className="bg-[#FFD60A] text-[#050810] font-black rounded-full px-8 py-4 hover:bg-[#FFEA00] shadow-[0_0_30px_rgba(255,214,10,0.35)] transition flex items-center justify-center gap-2">
                  <CalendarCheck className="w-5 h-5" /> Réserver un créneau <ChevronRight className="w-5 h-5" />
                </button>
                <a href="tel:0754896756" className="border-2 border-[#1A2642] bg-[#050810]/40 backdrop-blur text-white font-bold rounded-full px-8 py-4 hover:border-[#00B4D8] transition flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> 07 54 89 67 56
                </a>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-10">
                {[[ShieldCheck, "Pour tous véhicules"], [Gauge, "Résultat immédiat"], [CalendarCheck, "RDV obligatoire"]].map(([Icon, label]) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-[#CBD5E1]">
                    <Icon className="w-4 h-4 text-[#00B4D8]" /> {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: promo video */}
            <div className="relative animate-float-up lg:mt-0">
              <div className="absolute -inset-3 bg-[#00B4D8]/15 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-[#1A2642] shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
                <video
                  src={PROMO_VIDEO}
                  poster={HERO_IMG}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  data-testid="promo-video"
                  className="w-full aspect-video object-cover bg-black"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* SERVICES */}
      <section id="prestations" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-3">Nos prestations</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Choisissez votre service</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => {
            const Icon = [Thermometer, Wind, Gauge, ShieldCheck][i % 4];
            const active = selected?.id === s.id;
            return (
              <button key={s.id} onClick={() => chooseService(s)} data-testid={`service-card-${i}`}
                className={`text-left glass border rounded-lg p-6 transition group hover:-translate-y-1 ${active ? "border-[#00B4D8] ring-1 ring-[#00B4D8]" : "border-[#1A2642] hover:border-[#00B4D8]/60"}`}>
                <div className="w-11 h-11 rounded-full bg-[#00B4D8]/10 border border-[#00B4D8]/30 flex items-center justify-center mb-4 group-hover:bg-[#00B4D8]/20 transition">
                  <Icon className="w-5 h-5 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading font-bold text-lg leading-tight mb-1">{s.name}</h3>
                <p className="text-sm text-[#94A3B8] line-clamp-3 min-h-[3.6rem]">{s.description}</p>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#1A2642]">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-[#00B4D8]"><Clock className="w-3.5 h-3.5" />{s.duration_minutes} min</span>
                  <span className="font-heading text-2xl font-black text-[#FFD60A]">{s.price} €</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* BOOKING */}
      <section ref={bookingRef} id="reservation" className="max-w-3xl mx-auto px-5 md:px-10 pb-24 scroll-mt-20">
        <div className="mb-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-3">Réservation en ligne</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Réservez votre rendez-vous</h2>
        </div>
        <BookingFlow service={selected} settings={settings} customer={customer} onReset={() => { setSelected(null); document.getElementById("prestations")?.scrollIntoView({ behavior: "smooth" }); }} />
      </section>

      {/* HOURS + FOOTER */}
      <footer className="border-t border-[#1A2642] bg-[#0C1322]/40">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="mb-4">
              <Logo className="h-11 w-auto" testid="footer-logo" />
            </div>
            <p className="text-sm text-[#94A3B8]">Spécialiste de la recharge et de l'entretien de climatisation automobile à Istres.</p>
            <a href="tel:0754896756" className="inline-flex items-center gap-2 mt-4 text-[#FFD60A] font-bold"><Phone className="w-4 h-4" /> 07 54 89 67 56</a>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-4">Horaires d'ouverture</p>
            <ul className="space-y-1.5 text-sm">
              {settings && WEEKDAYS_FULL.map((day, idx) => {
                const cfg = settings.weekly_hours?.[String(idx)];
                return (
                  <li key={day} className="flex justify-between text-[#94A3B8]">
                    <span>{day}</span>
                    <span className={cfg?.open ? "text-white" : "text-[#475569]"}>{cfg?.open ? `${cfg.start} – ${cfg.end}` : "Fermé"}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-4">Infos</p>
            <ul className="space-y-2 text-sm text-[#94A3B8]">
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00B4D8]" /> Istres, Bouches-du-Rhône</li>
              <li className="flex items-center gap-2"><Car className="w-4 h-4 text-[#00B4D8]" /> Tous véhicules</li>
              <li className="flex items-center gap-2"><Thermometer className="w-4 h-4 text-[#00B4D8]" /> Paiement sur place</li>
            </ul>
            <a href="/admin/login" className="inline-block mt-5 text-xs text-[#475569] hover:text-[#94A3B8] transition" data-testid="admin-link">Espace gérant</a>
          </div>
        </div>
        <div className="border-t border-[#1A2642] py-5 flex flex-col items-center gap-3 text-center">
          <p className="text-xs text-[#475569]">© {new Date().getFullYear()} ClimOmax13 · Istres</p>
          <a href="https://agence-msc.fr" target="_blank" rel="noreferrer" data-testid="agency-credit"
            className="inline-flex items-center gap-2.5 text-xs text-[#64748B] hover:text-white transition group">
            <span>Réalisé par</span>
            <img src={MSC_LOGO} alt="Agence MSC" className="h-5 w-auto mix-blend-screen opacity-90 group-hover:opacity-100 transition" />
          </a>
        </div>
      </footer>

      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
